import React, { useContext, useMemo, useState } from 'react';
import { Box, Typography, Avatar, Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/context';
import {
  getToken,
  removeToken,
} from './authentication/functions/authentication_management';
import type { DecodedAuthToken } from './authentication/types/AuthTypes';
import { useResolvedAdminAccess } from './admin/useResolvedAdminAccess';
import { useFeatureFlags } from '../services/featureFlags/useFeatureFlags';
import { updateAgentContract } from '../automation/agentAutomation';
import { opsColors } from '../theme/opsTokens';
import { Button as OpsButton, IconButton as OpsIconButton } from '@liftango/ui-elements';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../utils/iconFallbacks';
const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const context = useContext(AppContext);
  const { t } = useTranslation('navigation');
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
  
  const userDetails = context?.userData?.userDetails || {
    userId: '',
    email: '',
    fullName: '',
    role: 'user' as const,
    customerId: null,
    customerName: null,
    companyRole: null,
  };
  const isAdmin = useResolvedAdminAccess();
  const { isEnabled } = useFeatureFlags();
  const isCompanyAdmin = useMemo(() => {
    if (userDetails.companyRole === 'company_admin') return true;
    const token = getToken();
    if (!token?.access_token) return false;
    try {
      const decoded = jwtDecode<DecodedAuthToken>(token.access_token);
      return decoded.company_role === 'company_admin';
    } catch {
      return false;
    }
  }, [userDetails.companyRole]);
  const isUserMenuOpen = Boolean(userMenuAnchorEl);
  const userMenuId = 'user-menu';

  const getProfileAvatarText = () => {
    const fullName = userDetails.fullName?.trim() || '';
    if (fullName) {
      const nameParts = fullName.split(/\s+/).filter(Boolean);
      const initials = nameParts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('');
      if (initials) return initials;
    }

    const email = userDetails.email?.trim() || '';
    if (email) {
      return email.charAt(0).toUpperCase();
    }

    return 'U';
  };

  // Docs site base (for profile menu tutorials link)
  const docsSiteBase =
    process.env.REACT_APP_DOCS_URL ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:5173' : '/docs/');
  // CloudFront with an OAC + S3 origin doesn't auto-resolve `/foo/` to
  // `/foo/index.html` (default_root_object only applies to the bucket root,
  // not sub-paths). Linking straight at the file makes the first load
  // resolve cleanly; once VitePress hydrates, its client router takes over.
  const tutorialsUrl = `${docsSiteBase.replace(/\/$/, '')}/tutorials/index.html`;

  const navigationItems = useMemo(() => [
    {
      key: 'wizard' as const,
      label: t('items.map'),
      path: '/',
      description: t('descriptions.map'),
      isExternal: false,
      isWizardTrigger: false,
    },
    {
      key: 'simulationResults' as const,
      label: t('items.simulationResults'),
      path: '/simulation-results',
      description: t('descriptions.simulationResults'),
      isExternal: false,
      isWizardTrigger: false,
    },
    {
      key: 'wizardItem' as const,
      label: t('items.wizard'),
      path: '/',
      description: t('descriptions.wizard'),
      isExternal: false,
      isWizardTrigger: true,
    },
    {
      key: 'tools' as const,
      label: t('items.tools'),
      path: '/tools',
      description: t('descriptions.tools'),
      isExternal: false,
      isWizardTrigger: false,
    },
  ], [t]);

  React.useEffect(() => {
    updateAgentContract({
      availablePrimaryActions: navigationItems.map((item) => `navigate:${item.path}`),
    });
  }, [navigationItems]);

  return (
    <Box
      data-testid="app.nav.root"
      data-auth-state={userDetails.userId ? 'authenticated' : 'anonymous'}
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.5,
        backgroundColor: opsColors.neutral.panel,
        width: '100%',
      }}
    >
        {/* Logo Section */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <a
            href="https://www.liftango.com/public-transport/labs"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              marginRight: '32px'
            }}
          >
            <img
              src="/Liftango_Logo.svg"
              alt="Liftango Labs"
              style={{ height: 40, width: 'auto' }}
            />
          </a>
          
          <OpsButton
            kind="ghost"
            size="sm"
            onClick={() => navigate('/')}
            style={{
              color: 'var(--liftango-neutral-ink, #171a1f)',
              fontWeight: 500,
              letterSpacing: '0.5px',
              textTransform: 'none',
              fontSize: '1.25rem',
              lineHeight: 1.6,
              minWidth: 'auto',
              padding: 0,
            }}
            aria-label={t('aria.goToMap')}
            data-testid="app.nav.go-to-map"
          >
            Atlas
          </OpsButton>
        </Box>

        {/* Right Cluster: navigation + profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, minWidth: 200, justifyContent: 'flex-end' }}>
          <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}>
            {navigationItems.map((item) => {
              const isActive = !item.isExternal && !item.isWizardTrigger && location.pathname === item.path;

              return (
                <OpsButton
                  key={`${item.key}-${item.path}`}
                  kind="ghost"
                  size="sm"
                  onClick={() => {
                    if (item.isExternal) {
                      const url = item.path.startsWith('http://') || item.path.startsWith('https://')
                        ? item.path
                        : `${window.location.origin}${item.path}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    } else if (item.isWizardTrigger) {
                      if (location.pathname !== '/') {
                        localStorage.removeItem('atlas_onboardingWizard_seen_v1');
                        navigate('/');
                      } else {
                        window.dispatchEvent(new Event('openOnboardingWizard'));
                      }
                    } else {
                      navigate(item.path);
                    }
                  }}
                  style={{
                    color: isActive ? opsColors.brand.primary : opsColors.neutral.ink,
                    fontWeight: isActive ? 700 : 500,
                    letterSpacing: '0.2px',
                    textTransform: 'none',
                    minWidth: 'auto',
                    borderBottom: isActive ? `2px solid ${opsColors.brand.primary}` : '2px solid transparent',
                    borderRadius: 0,
                    padding: '0 12px',
                    height: 48,
                  }}
                  aria-label={t('aria.navigateTo', { label: item.label, description: item.description })}
                  data-testid={`app.nav.item.${item.key}`}
                >
                  {item.label}
                </OpsButton>
              );
            })}
            {isCompanyAdmin && (
              <OpsButton
                kind="ghost"
                size="sm"
                onClick={() => navigate('/company')}
                style={{
                  color: location.pathname === '/company' ? opsColors.brand.primary : opsColors.neutral.ink,
                  fontWeight: location.pathname === '/company' ? 700 : 500,
                  letterSpacing: '0.2px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  borderBottom: location.pathname === '/company' ? `2px solid ${opsColors.brand.primary}` : '2px solid transparent',
                  borderRadius: 0,
                  padding: '0 12px',
                  height: 48,
                }}
                aria-label={t('aria.navigateToCompany')}
                data-testid="app.nav.item.company"
              >
                {t('items.company')}
              </OpsButton>
            )}
            {isEnabled('rules_engine') && (
              <OpsButton
                kind="ghost"
                size="sm"
                onClick={() => navigate('/rules-engine')}
                style={{
                  color: location.pathname === '/rules-engine' ? opsColors.brand.primary : opsColors.neutral.ink,
                  fontWeight: location.pathname === '/rules-engine' ? 700 : 500,
                  letterSpacing: '0.2px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  borderBottom: location.pathname === '/rules-engine' ? `2px solid ${opsColors.brand.primary}` : '2px solid transparent',
                  borderRadius: 0,
                  padding: '0 12px',
                  height: 48,
                }}
                aria-label="Navigate to Rules Engine"
                data-testid="app.nav.item.rules-engine"
              >
                Rules Engine
              </OpsButton>
            )}
            {isAdmin && (
              <OpsButton
                kind="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                style={{
                  color: location.pathname === '/admin' ? opsColors.brand.primary : opsColors.neutral.ink,
                  fontWeight: location.pathname === '/admin' ? 700 : 500,
                  letterSpacing: '0.2px',
                  textTransform: 'none',
                  minWidth: 'auto',
                  borderBottom: location.pathname === '/admin' ? `2px solid ${opsColors.brand.primary}` : '2px solid transparent',
                  borderRadius: 0,
                  padding: '0 12px',
                  height: 48,
                }}
                aria-label={t('aria.navigateToAdmin')}
                data-testid="app.nav.item.admin"
              >
                {t('items.admin')}
              </OpsButton>
            )}
          </Box>

          {userDetails.userId ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <OpsIconButton
                variant="ghost"
                label="Open user menu"
                icon={(
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem', cursor: 'pointer' }}>
                    {getProfileAvatarText()}
                  </Avatar>
                )}
                onClick={(event) => setUserMenuAnchorEl(event.currentTarget)}
                aria-label={t('aria.openUserMenu')}
                aria-controls={isUserMenuOpen ? userMenuId : undefined}
                aria-haspopup="menu"
                aria-expanded={isUserMenuOpen ? 'true' : undefined}
                data-testid="app.auth.user-menu-toggle"
                style={{ padding: '0 4px', borderRadius: 999 }}
              />

              <Menu
                id={userMenuId}
                anchorEl={userMenuAnchorEl}
                open={isUserMenuOpen}
                onClose={() => setUserMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 2700 }}
                slotProps={{
                  paper: {
                    sx: { zIndex: 2700 },
                  },
                }}
              >
                {userDetails.customerName && (
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{userDetails.customerName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {userDetails.companyRole === 'company_admin'
                        ? t('userMenu.companyAdmin')
                        : t('userMenu.companyMember')}
                    </Typography>
                  </Box>
                )}
                {userDetails.customerName && <Divider />}
                <MenuItem
                  onClick={() => {
                    setUserMenuAnchorEl(null);
                    navigate('/profile');
                  }}
                >
                  <ListItemIcon>
                    <Icon d={ICON_FALLBACKS.AccountCircle} size={20} />
                  </ListItemIcon>
                  <ListItemText>{t('userMenu.account')}</ListItemText>
                </MenuItem>
                <Divider />
                {tutorialsUrl && (
                  <MenuItem
                    onClick={() => {
                      setUserMenuAnchorEl(null);
                      const url = tutorialsUrl.startsWith('http://') || tutorialsUrl.startsWith('https://')
                        ? tutorialsUrl
                        : `${window.location.origin}${tutorialsUrl}`;
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <ListItemIcon>
                      <Icon d={ICON_FALLBACKS.MenuBook} size={20} />
                    </ListItemIcon>
                    <ListItemText>{t('userMenu.tutorials')}</ListItemText>
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setUserMenuAnchorEl(null);
                    navigate('/credits');
                  }}
                >
                  <ListItemIcon>
                    <Icon d={ICON_FALLBACKS.DescriptionOutlined} size={20} />
                  </ListItemIcon>
                  <ListItemText>{t('userMenu.credits')}</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setUserMenuAnchorEl(null);
                    removeToken();
                    if (context?.userData?.setUserDetails) {
                      context.userData.setUserDetails({
                        email: '',
                        fullName: '',
                        userId: '',
                        role: 'user',
                        customerId: null,
                        customerName: null,
                        companyRole: null,
                      });
                    }
                  }}
                >
                  <ListItemIcon>
                    <Icon d={ICON_FALLBACKS.Logout} size={20} />
                  </ListItemIcon>
                  <ListItemText>{t('userMenu.logout')}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          ) : null}
        </Box>
    </Box>
  );
};

export default Navigation;
